import React, { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { FiX } from "react-icons/fi";
import classNames from "./classNames";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

const Modal = ({
  open,
  onClose,
  title,
  children,
  className,
  size = "medium",
  closeLabel = "Close dialog",
}) => {
  const dialogRef = useRef(null);
  const returnFocusRef = useRef(null);
  const titleId = `modal-title-${useId().replace(/:/g, "")}`;

  useEffect(() => {
    if (!open) return undefined;

    returnFocusRef.current = document.activeElement;
    document.body.classList.add("has-open-modal");
    const dialog = dialogRef.current;
    const firstFocusable = dialog?.querySelector(FOCUSABLE_SELECTOR);
    (firstFocusable || dialog)?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose?.();
        return;
      }

      if (event.key !== "Tab" || !dialog) return;
      const focusable = Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR));
      if (!focusable.length) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.classList.remove("has-open-modal");
      returnFocusRef.current?.focus?.();
    };
  }, [onClose, open]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div className="modal" role="presentation">
      <button className="modal__backdrop" type="button" onClick={onClose} aria-label={closeLabel} />
      <section
        ref={dialogRef}
        className={classNames("modal__dialog", `modal__dialog--${size}`, className)}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={title ? undefined : "Dialog"}
        tabIndex="-1"
      >
        <header className="modal__header">
          {title && <h2 id={titleId}>{title}</h2>}
          <button className="modal__close" type="button" onClick={onClose} aria-label={closeLabel}>
            <FiX aria-hidden="true" />
          </button>
        </header>
        <div className="modal__body">{children}</div>
      </section>
    </div>,
    document.body
  );
};

export default Modal;
